import React, { useCallback, type ChangeEvent, type HTMLProps } from 'react';

import toCamelCase from '../../../utils/toCamelCase';
import cx from '../../../utils/classNames';

import styles from '../textArea.module.scss';
import { countCodePoints } from '@travel/utils';

type Props = {
  /** Disabled state */
  isDisabled?: boolean;
  /** Character limit for the textarea */
  characterLimit?: number;
  /** Error state */
  hasError?: boolean;
  /* Function to count characters typed in the input*/
  handleTextFieldCharacterCount?: (str: string, locale?: string) => number;
  /** Reference to the textarea */
  inputRef?: React.Ref<HTMLTextAreaElement>;
  /** Function to handle change event */
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
} & Omit<HTMLProps<HTMLTextAreaElement>, 'ref' | 'disabled' | 'onChange'>;

const InputArea = (props: Props) => {
  const {
    className,
    characterLimit,
    onChange,
    handleTextFieldCharacterCount = countCodePoints,
    hasError,
    isDisabled,
    inputRef,
    ...rest
  } = props;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      event.persist();

      if (
        characterLimit !== undefined &&
        handleTextFieldCharacterCount(event.currentTarget.value) > characterLimit
      ) {
        return false;
      }

      onChange?.(event);
      return true;
    },
    [characterLimit, onChange],
  );

  const classNames = cx(
    className,
    styles[toCamelCase('textArea', 'input')],
    hasError ? styles[toCamelCase('textArea', 'error')] : '',
    isDisabled ? styles[toCamelCase('textArea', 'disabled')] : '',
  );

  return (
    <textarea
      data-testid="textArea-input"
      className={classNames}
      disabled={isDisabled}
      onChange={handleChange}
      ref={inputRef}
      {...rest}
    />
  );
};

export default InputArea;
